import { baseApi } from '@/Redux/baseApi';

/**
 * # The whole ADR-0003 `api-clients` contract, pinned in one file
 *
 * Read directly from `src/modules/public-api/` on the backend's `main` (merged,
 * production — ADR-0003, "`/public/v1` is a separate surface, with a third kind
 * of token"):
 *   - `api-clients.controller.ts` for every route below — this is the
 *     INTERNAL, staff-permissioned side. `/public/v1` itself has no route that
 *     administers a credential; only `POST /api-clients` and friends do.
 *   - `dto/api-client.dto.ts` for the request bodies
 *   - `api-clients.service.ts` for the response shape (`CLIENT_VIEW` — never
 *     the hash, never anything the secret could be rebuilt from)
 *   - `scopes.ts` for `API_SCOPES` — copied verbatim rather than re-derived,
 *     because a scope this screen offers that the backend does not grant is a
 *     checkbox promising nothing, and `scope-catalogue.spec.ts` is what keeps
 *     the backend's own list truthful against its routes. Five of the nine
 *     below (`branches:read`, `customers:read`, `payments:read`, `menus:read`,
 *     `inventory:read`) are not on `dev` yet — pulled forward from
 *     `feat/public-api-surface` (fetched, not yet merged: ten new
 *     `/public/v1` routes across five new scopes) because a scope picker that
 *     only lists what has shipped omits exactly the reads a venue is about to
 *     be asked to grant.
 *
 * ## What each scope actually hands over is in the operator's own words
 *
 * `API_SCOPES`' values below are the backend's own one-line technical
 * comments — useful as a secondary label, never the primary one. The
 * sentence a person actually has to read before checking a box —
 * `integrations.apiClients.scopeDescription.<key>` in both locale files —
 * says what a third party's server GETS, in second person: granting
 * `customers:read` is handing a third party this venue's customer list, and
 * the sentence says exactly that rather than "Read customer profiles."
 * `scopeI18nKey()` exists because i18next's default `nsSeparator` is `:`, the
 * exact character every scope string contains, so `t(...${scope})` would be
 * parsed as a namespace split rather than a literal key.
 *
 * ## The secret is returned exactly once, at creation and at rotation
 *
 * `ApiClientWithSecret` is the answer to `create` and `rotateSecret`, and
 * nothing else in this file ever carries `clientSecret`. There is no "reveal"
 * route and this file defines no way to ask for one — `secretHash` is a
 * one-way SHA-256, not recoverable, by design. Hand the value straight to
 * `SecretRevealDialog` (`Pages/Integrations/components/SecretRevealDialog.tsx`,
 * built for the identical webhook-signing-secret guarantee) and never round-trip
 * it through the RTK Query cache.
 *
 * ## `.update` and `.revoke` are two different permission keys, on purpose
 *
 * `ApiClientsController`'s own comment: rotation is `api_clients.update`
 * because it HANDS OUT a working credential; revocation is `api_clients.revoke`,
 * the key that only ever takes access away, kept separate "so an on-call
 * responder can be given the ability to kill a leaked secret without the
 * ability to mint one." `API_CLIENTS_PERMISSIONS` below keeps that split
 * visible rather than folding both into one "manage" key — collapsing them
 * would silently regrant the responder the power the split exists to withhold.
 *
 * ## Rotation from THIS route never accepts an overlap window
 *
 * `RotateApiClientSecretDto` exists on the backend and documents an
 * `overlapMinutes` field, but `ApiClientsController.rotate` declares no
 * `@Body()` parameter at all — the request body is never bound, so anything a
 * caller of this route sends is silently discarded and `rotateSecret` always
 * runs with its default (`overlapMinutes = 0`, the old secret dead the instant
 * this returns). Only the developer portal's OWN sandbox-credential rotation
 * route (`POST /devportal/apps/:id/sandbox/credential/rotate`) wires a caller
 * value through. This screen therefore offers no overlap control — an input
 * with no effect on the server is worse than no input — and says plainly that
 * rotating here is immediate.
 */

export const API_SCOPES = {
  'catalog:read': 'Read the sellable catalogue — items, prices, availability.',
  'orders:read': 'Read orders and their lines.',
  'orders:write': 'Place a takeaway order, priced by the venue.',
  'checks:read': 'Read bills and their totals.',
  'branches:read': 'Read a venue’s branches and their public details.',
  'customers:read': 'Read customer profiles — name, contact and CRM identity.',
  'payments:read': 'Read the payment ledger — settlements, refunds and tips.',
  'menus:read': 'Read menus — which items they carry, and on what schedule.',
  'inventory:read': 'Read inventory items and their stock on hand.',
} as const;

export type ApiScope = keyof typeof API_SCOPES;

export const ALL_API_SCOPES = Object.keys(API_SCOPES) as ApiScope[];

/**
 * A scope string into a translation-key leaf — see the header note on why
 * i18next's default `:` namespace separator rules out using the scope
 * itself. `catalog:read` → `catalogRead`.
 */
export function scopeI18nKey(scope: ApiScope): string {
  const [resource, action] = scope.split(':');
  return `${resource}${action.charAt(0).toUpperCase()}${action.slice(1)}`;
}

export type ApiClientStatus = 'ACTIVE' | 'SUSPENDED' | 'REVOKED';

export const API_CLIENTS_ROUTES = {
  list: '/api-clients',
  one: (id: string) => `/api-clients/${id}`,
  rotateSecret: (id: string) => `/api-clients/${id}/rotate-secret`,
} as const;

/**
 * `view`/`create`/`update`/`revoke` — the four keys `permissions.ts` registers
 * under `api_clients`. `update` alone covers rename, re-scope, re-fence,
 * suspend/resume AND rotate; `revoke` is its own key. See the header note.
 */
export const API_CLIENTS_PERMISSIONS = {
  view: 'api_clients.view',
  create: 'api_clients.create',
  update: 'api_clients.update',
  revoke: 'api_clients.revoke',
} as const;

/** `CLIENT_VIEW` on the wire. Never `secretHash`, `previousSecretHash`, or anything a secret could be rebuilt from. */
export interface ApiClient {
  id: string;
  clientId: string;
  /** The first characters of the secret — enough to tell two credentials apart, never enough to rebuild one. */
  secretPrefix: string;
  name: string;
  description: string | null;
  scopes: ApiScope[];
  /** When set, the only branch this client may read or write — a fence, not a default. */
  branchId: string | null;
  status: ApiClientStatus;
  rateLimitPerMinute: number;
  /** Requests per UTC day, or null for no daily ceiling. This is the CEILING, not a live count — see `ApiClientsPanel`'s note. */
  quotaPerDay: number | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
}

/** The one extra field `create` and `rotateSecret` answer with. Shown once and never fetched again. */
export interface ApiClientWithSecret extends ApiClient {
  clientSecret: string;
}

export interface CreateApiClientBody {
  name: string;
  description?: string;
  scopes: ApiScope[];
  branchId?: string;
  rateLimitPerMinute?: number;
  quotaPerDay?: number;
}

/**
 * Deliberately not every field `CreateApiClientBody` has, and `status` appears
 * ONLY here — mirroring `UpdateApiClientDto`'s own comment: the fields a
 * credential gains after it exists are not the fields it was created with.
 * `status` may only be set to `ACTIVE` or `SUSPENDED`; the backend refuses
 * `REVOKED` on this route with a message pointing at `DELETE /api-clients/:id`
 * instead, and refuses this route entirely once a client is already revoked —
 * a revoked credential is not editable back to life.
 */
export interface UpdateApiClientBody {
  name?: string;
  description?: string;
  scopes?: ApiScope[];
  /** `null` clears the branch fence — the credential sees the whole venue. */
  branchId?: string | null;
  status?: 'ACTIVE' | 'SUSPENDED';
  rateLimitPerMinute?: number;
  quotaPerDay?: number | null;
}

export const apiClientsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /api-clients` — `api_clients.view`. */
    apiClients: build.query<ApiClient[], void>({
      query: () => ({ url: API_CLIENTS_ROUTES.list }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? (response as ApiClient[]) : []),
      providesTags: (result) => [
        { type: 'ApiClient' as const, id: 'LIST' },
        ...(result ?? []).map((c) => ({ type: 'ApiClient' as const, id: c.id })),
      ],
    }),

    /** `GET /api-clients/:id` — `api_clients.view`. */
    apiClient: build.query<ApiClient | null, string>({
      query: (id) => ({ url: API_CLIENTS_ROUTES.one(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as ApiClient) : null,
      providesTags: (_r, _e, id) => [{ type: 'ApiClient' as const, id }],
    }),
  }),
  overrideExisting: false,
});

export const { useApiClientsQuery, useApiClientQuery } = apiClientsApi;

/** A client changed — created, edited, rotated, suspended, resumed or revoked. */
export function invalidateApiClient(id?: string) {
  return apiClientsApi.util.invalidateTags([
    { type: 'ApiClient' as const, id: 'LIST' },
    ...(id ? [{ type: 'ApiClient' as const, id }] : []),
  ]);
}
